<script setup lang="ts">
// Import libraries
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { useScrollLock } from '~/utils/useScrollLock';

// Import components
import BrandLink from '~/components/site-header/BrandLink.vue';
import CloseButton from '~/components/site-header/CloseButton.vue';
import MainNavigation from '~/components/site-header/MainNavigation.vue';
import LangSwitch from '~/components/site-header/LangSwitch.vue';
import MenuTeaser from '~/components/site-header/MenuTeaser.vue';
import MenuAddress from '~/components/site-header/MenuAddress.vue';
import MenuContact from '~/components/site-header/MenuContact.vue';
import Blobs from '~/components/site-header/Blobs.vue';

const { localeProperties } = useI18n();

// Fetch data
const { data } = await useAsyncGql({
  operation: 'GetMainNavigation',
  variables: { locale: localeProperties.value.siteLocale },
});

const props = defineProps<{
  isOpen: boolean;
}>();

const emit = defineEmits<{
  (e: 'close-nav'): void;
}>();

// Get data
const mainNavigation = ref(data.value.mainNavigation);

// Computed data
const navLinks = computed(() => mainNavigation.value?.navLinks || []);
const navigationTeaser = computed(() => {
  return mainNavigation?.value?.navigationTeaser ?? {};
});

// Refs
const overlayRef = ref<HTMLElement | null>(null);
const closeButtonRef = ref<InstanceType<typeof CloseButton> | null>(null);

// Focus trap integration
const { activate, deactivate } = useFocusTrap(overlayRef, {
  immediate: true,
});

// Handle menu open/close
watch(
  () => props.isOpen,
  (val) => {
    nextTick(() => {
      handleScrollLock(val);
      handleFocusTrap(val);
    });
  },
);

// Handle focus trap
const handleFocusTrap = (val: boolean) => {
  if (val) {
    activate();
    closeButtonRef.value?.setFocus();
  } else {
    deactivate();
  }
};

// Handle scroll lock
const handleScrollLock = (val: boolean) => {
  if (val) {
    useScrollLock(true);
  } else {
    useScrollLock(false);
  }
};

// Close menu on ESC
const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    handleClose();
  }
};

const handleClose = () => {
  emit('close-nav');
};

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', onKeydown);
  }
});

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown);
  useScrollLock(false);
});
</script>

<template>
  <Teleport to="body">
    <Transition name="menu-overlay-transition">
      <div
        v-if="isOpen"
        ref="overlayRef"
        class="menu-overlay"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('menuOverlay')"
      >
        <div class="header">
          <BrandLink />
          <CloseButton @click="handleClose" ref="closeButtonRef" />
        </div>
        <div class="content">
          <div class="navigation">
            <MainNavigation
              :nav-links="navLinks"
              :navigation-teaser="navigationTeaser"
              :is-open="isOpen"
            />
          </div>
          <div class="aside">
            <MenuTeaser
              class="_hidden-vs"
              :navigation-teaser="navigationTeaser"
            />
            <MenuAddress />
          </div>
        </div>
        <div class="footer">
          <div class="lang">
            <LangSwitch />
          </div>
          <div class="contact">
            <MenuContact />
          </div>
        </div>
        <Blobs />
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.menu-overlay-transition-enter-from,
.menu-overlay-transition-leave-to {
  clip-path: inset(100% 0 0 0);
}

.menu-overlay-transition-enter-to,
.menu-overlay-transition-leave-from {
  clip-path: inset(0 0 0 0);
}

.menu-overlay-transition-enter-active {
  transition: clip-path 1s var(--base-transition-easing);
}

.menu-overlay-transition-leave-active {
  transition: clip-path 0.35s var(--base-transition-easing);
}

.menu-overlay-transition-enter-from .footer {
  transform: translateY(75vh);
}

.menu-overlay-transition-enter-from .aside {
  transform: translateY(50vh);
}

.menu-overlay-transition-enter-to .footer,
.menu-overlay-transition-enter-to .aside {
  transform: translateY(0);
}

.menu-overlay-transition-enter-active .footer,
.menu-overlay-transition-enter-active .aside {
  transition: transform 1s var(--base-transition-easing);
}

.menu-overlay {
  --grid-full: 8;
  --grid-6: calc(100% / var(--grid-full) * 6);
  --grid-2: calc(100% / var(--grid-full) * 2);
  --content-gap: 2rem;
  --left-width: calc(var(--grid-6) - var(--content-gap) / 2);
  --right-width: calc(var(--grid-2) - var(--content-gap) / 2);

  z-index: 200;
  position: fixed;
  inset: 0;
  background: var(--base-color-navigation-background);
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  @media (--vs) {
    padding-inline: 1.25rem;
    padding-bottom: 2rem;
  }

  @media (--vl) {
    padding-inline: 5rem;
    padding-bottom: 3rem;
  }
}

.menu-overlay > .header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media (--vs) {
    padding-block: 1.25rem;
  }

  @media (--vl) {
    padding-block: 3.5rem 2.5rem;
  }
}

.menu-overlay > .content {
  display: flex;
  gap: var(--content-gap);
  flex: 1;

  @media (--vs) {
    flex-direction: column;
    justify-content: space-between;
  }
}

.menu-overlay > .content > .navigation {
  display: flex;
  flex-direction: column;
  padding-block: 1rem;

  @media (--vs) {
    height: 100%;
    justify-content: center;
    gap: 2rem;
  }

  @media (--vl) {
    width: var(--left-width);
    justify-content: space-between;
  }
}

.menu-overlay > .content > .aside {
  display: flex;
  flex-direction: column;
  padding-bottom: 2rem;

  @media (--vl) {
    gap: 2rem;
    justify-content: space-between;
    width: var(--right-width);
  }
}

.menu-overlay > .footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  @media (--vs) {
    flex-direction: row-reverse;
    align-items: flex-end;
  }
}

.menu-overlay > .footer > .lang {
  @media (--vl) {
    width: var(--left-width);
  }
}

.menu-overlay > .footer > .contact {
  @media (--vl) {
    width: var(--right-width);
  }
}
</style>
